import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  ApiError,
  CondLimiteEnum,
  Pacote,
  PacoteRequest,
  ProdutoDocs,
  ProdutoDocsRequest,
  ProdutoService,
  RefundPolicyEnum,
} from '../../../slidein_api';
import { fileLink, msgAPIError } from '../../helpers';
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  useMantineReactTable,
} from 'mantine-react-table';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  FileInput,
  Group,
  Modal,
  NumberInput,
  Select,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconCircleCheck,
  IconCircleX,
  IconCurrencyEuro,
  IconDeviceFloppy,
  IconEdit,
  IconFiles,
  IconMessageCircle,
  IconNotes,
  IconPackage,
  IconPackages,
  IconPdf,
  IconPlus,
  IconQuestionMark, IconReportMoney,
  IconTrash,
  IconTrashX,
  IconUpload,
  IconUser,
  IconUsersGroup,
  IconX,
} from '@tabler/icons-react';
import { MRT_Localization_PT } from 'mantine-react-table/locales/pt';
import { useForm, yupResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { checkPermission, MASTER } from '../../../components/CheckPermission';
import { useAuth } from '../../../user-auth/SlideInAuthContext';

const EMPTYPACOTE: PacoteRequest = {
  descricao: '',
  nome_interno: '',
  titulo_cliente: '',
  valor: undefined,
  campanha: '',
  activo: true,
  inicial: false,
  inscricao_previa: false,
  msg_metodo_inscricao: '',
  refund_policy: RefundPolicyEnum.SEM_REEMBOLSO,
};

export function PacoteManager() {
  const { colaborador } = useAuth();
  const params = useParams();
  const [dialogParams, setDialogParams] = useState<{
    create: boolean;
    visible: boolean;
  }>({
    create: true,
    visible: false,
  });
  const [update, setUpdate] = useState<number>(0);
  const [pacotes, setPacotes] = useState<Pacote[]>([]);
  const [pacoteDocsEdit, setPacoteDocEdit] = useState<{ pacote: Pacote; docs: ProdutoDocs[] }>(
    undefined
  );
  const [docsDiag, setDocsDialog] = useState<boolean>(false);
  const [docsDiagAdd, setDocsDialogAdd] = useState<boolean>(false);

  useEffect(() => {
    ProdutoService.produtoPacoteList({ campanha: params.campanhaID }).then(
      (value) => {
        setPacotes(value);
      },
      (reason) => msgAPIError(reason)
    );
  }, [update]);

  const pacoteForm = useForm<Pacote | PacoteRequest>({
    initialValues: EMPTYPACOTE,
    validate: yupResolver(
      Yup.object({
        nome_interno: Yup.string().min(4, 'Tamanho mínimo de 4 caracteres').required('Obrigatório'),
        titulo_cliente: Yup.string().required('Obrigatório'),
        valor: Yup.number().min(0).required('Obrigatório'),
      })
    ),
  });
  const savePacote = async (pacote: Pacote) => {
    if (dialogParams.create) {
      pacote.campanha = params.campanhaID;
      ProdutoService.produtoPacoteCreate({ requestBody: pacote }).then(
        () => {
          setUpdate(update + 1);
          setDialogParams({ create: true, visible: false });
        },
        (error) => {
          pacoteForm.setErrors(error.body);
        }
      );
    } else {
      ProdutoService.produtoPacoteUpdate({
        id: pacote.id,
        requestBody: pacote,
      }).then(
        () => {
          setUpdate(update + 1);
          setDialogParams({ create: true, visible: false });
          notifications.show({
            title: `Alojamento/Pacote atualizado!`,
            message: `O Alojamento/Pacote ${pacote.nome_interno} foi atualizado.`,
            withCloseButton: true,
            icon: <IconCircleCheck />,
            color: 'green',
          });
        },
        (error) => {
          pacoteForm.setErrors(error.body);
        }
      );
    }
  };

  function criarPacote() {
    pacoteForm.reset();
    setDialogParams({
      create: true,
      visible: true,
    });
  }

  function editPacote(pacote: Pacote) {
    pacoteForm.reset();
    pacoteForm.setValues(pacote);
    setDialogParams({
      create: false,
      visible: true,
    });
  }

  function removePacote(pacote: Pacote) {
    if (
      confirm('Confirma que deseja remover o alojamento/pacote : ' + pacote.nome_interno + ' ?')
    ) {
      ProdutoService.produtoPacoteDestroy({ id: pacote.id }).then(
        () => {
          setUpdate(update + 1);
          notifications.show({
            title: 'Alojamento/Pacote removido!',
            message: `O alojamento/pacote ${pacote.nome_interno} foi removido.`,
            withCloseButton: true,
            icon: <IconTrashX />,
            color: 'red',
          });
        },
        (reason) => {
          msgAPIError(reason);
        }
      );
    }
  }

  const editPacoteDialog = (
    <Modal
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      size={'auto'}
      opened={dialogParams.visible}
      onClose={() => {
        setDialogParams({ create: false, visible: false });
      }}
      title={dialogParams.create ? 'Criar Alojamento/Pacote' : 'Editar Alojamento/Pacote'}>
      <form onSubmit={pacoteForm.onSubmit(savePacote)}>
        <TextInput
          label={'Nome Interno'}
          icon={<IconPackage />}
          {...pacoteForm.getInputProps('nome_interno')}
        />
        <Text color={'red'}>{pacoteForm.errors['non_field_errors']}</Text>
        <TextInput
          label={'Titulo Cliente'}
          icon={<IconUser />}
          {...pacoteForm.getInputProps('titulo_cliente')}
        />

        <NumberInput
          placeholder='Valor'
          label='Valor'
          precision={2}
          icon={<IconCurrencyEuro />}
          hideControls
          decimalSeparator=','
          thousandsSeparator='.'
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          formatter={(value) =>
            !Number.isNaN(parseFloat(value))
              ? // eslint-disable-next-line security/detect-unsafe-regex
                `${value} €`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
              : ''
          }
          {...pacoteForm.getInputProps('valor')}
        />
        {!dialogParams.create && (
          <Text color={'red'}>
            Não deve alterar o valor após criado/vendido! Criar um Pacote novo e desativar este!
          </Text>
        )}
        <Select
          label={'Reembolso:'}
          icon={<IconReportMoney />}
          data={Object.keys(RefundPolicyEnum).map((x) => {
            return {
              label: RefundPolicyEnum[x as keyof typeof RefundPolicyEnum],
              value: RefundPolicyEnum[x as keyof typeof RefundPolicyEnum],
            };
          })}
          {...pacoteForm.getInputProps('refund_policy')}
        />
        <NumberInput
          placeholder='Ilimitado'
          label='Unidades Disponiveis(Stock)'
          precision={0}
          description={'Deixar em branco para ser ilimitado.'}
          min={0}
          icon={<IconPackages />}
          thousandsSeparator='.'
          value={pacoteForm.values.unidades !== null ? pacoteForm.values.unidades : ''}
          onChange={(value) => {
            if (value !== '') {
              pacoteForm.setFieldValue('unidades', value);
            } else {
              pacoteForm.setFieldValue('unidades', null);
            }
          }}
        />
        <Select
          label={'Condição:'}
          icon={<IconQuestionMark />}
          data={Object.keys(CondLimiteEnum).map((x) => {
            return {
              label: CondLimiteEnum[x as keyof typeof CondLimiteEnum],
              value: CondLimiteEnum[x as keyof typeof CondLimiteEnum],
            };
          })}
          {...pacoteForm.getInputProps('cond_limite')}
        />

        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label=' Activo, disponivel para compra pelos clientes'
          color='green'
          radius='md'
          size='lg'
          {...pacoteForm.getInputProps('activo', { type: 'checkbox' })}
        />
        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label='Adicionar automáticamente aos Grupos (na criação)'
          color='green'
          radius='md'
          size='lg'
          {...pacoteForm.getInputProps('inicial', { type: 'checkbox' })}
        />
        <Checkbox
          mt={'lg'}
          labelPosition='right'
          label='Inscrição via "Outro  método"'
          color='green'
          radius='md'
          size='lg'
          {...pacoteForm.getInputProps('inscricao_previa', { type: 'checkbox' })}
        />
        {pacoteForm.values.inscricao_previa && (
          <TextInput
            label={'Método de Inscrição (Mensagem cliente)'}
            icon={<IconMessageCircle />}
            {...pacoteForm.getInputProps('msg_metodo_inscricao')}
          />
        )}

        <Textarea
          placeholder='Descrição...'
          label='Descrição'
          size='md'
          icon={<IconNotes />}
          autosize={true}
          minRows={2}
          maxRows={4}
          {...pacoteForm.getInputProps('descricao')}
        />
        <NumberInput
          label={'# Ordem de aparência clientes'}
          min={0}
          placeholder={'Em branco = último.'}
          value={
            pacoteForm.values.ordem_de_aparencia !== null
              ? pacoteForm.values.ordem_de_aparencia
              : ''
          }
          onChange={(value) => {
            if (value !== '') {
              pacoteForm.setFieldValue('ordem_de_aparencia', value);
            } else {
              pacoteForm.setFieldValue('ordem_de_aparencia', null);
            }
          }}
        />
        <Group
          position='right'
          mt='md'>
          <Button
            leftIcon={<IconDeviceFloppy />}
            variant={'outline'}
            type='submit'>
            Guardar
          </Button>
          <Button
            color={'yellow'}
            variant={'outline'}
            leftIcon={<IconX />}
            type='button'
            onClick={() => setDialogParams({ create: false, visible: false })}>
            Cancelar
          </Button>
        </Group>
      </form>
    </Modal>
  );

  //--------------------------------------------------------------
  const docForm = useForm<ProdutoDocsRequest>({
    initialValues: {
      nome: '',
      produto: undefined,
      file: undefined,
    },

    validate: yupResolver(
      Yup.object({
        nome: Yup.string().min(5, 'Tamanho mínimo de 5 caracteres').required('Obrigatório'),
        file: Yup.mixed().required('Nenhum ficheiro selecionado!'),
      })
    ),
  });
  const savePacoteDoc = async (doc: ProdutoDocsRequest) => {
    doc.produto = pacoteDocsEdit.pacote.id;
    ProdutoService.produtoDocsCreate({
      formData: doc,
    }).then(
      (value) => {
        setUpdate(update + 1);
        setPacoteDocEdit({
          pacote: pacoteDocsEdit.pacote,
          docs: [...pacoteDocsEdit.docs, value],
        });
        setDocsDialogAdd(false);
      },
      (error) => {
        docForm.setErrors(error.body);
      }
    );
  };

  function adicionarPacoteDoc() {
    docForm.reset();
    docForm.values.produto = pacoteDocsEdit.pacote.id;
    setDocsDialogAdd(true);
  }

  const columnsDocs = useMemo<MRT_ColumnDef<ProdutoDocs>[]>(
    () => [
      {
        accessorKey: 'nome',
        header: 'Nome Documento',
      },
      {
        accessorFn: (row) => fileLink(row.nome, row.file),
        header: 'Ficheiro',
      },
    ],
    []
  );

  const editDocsDialog = () => {
    if (pacoteDocsEdit === undefined) {
      return <></>;
    } else {
      return (
        <>
          <Modal
            withCloseButton={true}
            closeOnEscape={false}
            closeOnClickOutside={false}
            size={'xl'}
            title={`Documentos (${pacoteDocsEdit.pacote.nome_interno})`}
            opened={docsDiag && !docsDiagAdd}
            onClose={() => setDocsDialog(false)}>
            <MantineReactTable
              displayColumnDefOptions={{
                'mrt-row-actions': {
                  mantineTableHeadCellProps: {
                    align: 'center',
                  },
                  size: 120,
                },
              }}
              renderTopToolbarCustomActions={() => {
                return (
                  <Box sx={{ display: 'flex', gap: '16px', padding: '4px', flexWrap: 'wrap' }}>
                    <Button
                      disabled={!checkPermission(MASTER, colaborador)}
                      leftIcon={<IconPlus />}
                      variant='outline'
                      onClick={() => {
                        adicionarPacoteDoc();
                      }}>
                      Adicionar Documento
                    </Button>
                  </Box>
                );
              }}
              initialState={{
                density: 'xs',
                showGlobalFilter: true,
                pagination: { pageSize: 15, pageIndex: 0 },
              }}
              enableRowActions={checkPermission(MASTER, colaborador)}
              renderRowActions={({ row, table }) => {
                if (checkPermission(MASTER, colaborador)) {
                  return (
                    <Box sx={{ display: 'flex', gap: '16px' }}>
                      <Tooltip label='Editar'>
                        <ActionIcon
                          color='blue'
                          onClick={() => table.setEditingRow(row)}>
                          <IconEdit />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label='Remover'>
                        <ActionIcon
                          color='red'
                          onClick={() => {
                            if (confirm('Confirma que deseja remover o documento ?')) {
                              ProdutoService.produtoDocsDestroy({ id: row.original.id }).then(
                                () => {
                                  setUpdate(update + 1);
                                  setPacoteDocEdit({
                                    pacote: pacoteDocsEdit.pacote,
                                    docs: pacoteDocsEdit.docs.filter((pa) => {
                                      return pa.id !== row.original.id;
                                    }),
                                  });

                                  notifications.show({
                                    title: 'Documento removido!',
                                    message: `O documento ${row.original.nome}  foi removido.`,
                                    withCloseButton: true,
                                    icon: <IconTrashX />,
                                    color: 'red',
                                  });
                                },
                                (reason) => {
                                  msgAPIError(reason);
                                }
                              );
                            }
                          }}>
                          <IconTrashX />
                        </ActionIcon>
                      </Tooltip>
                    </Box>
                  );
                } else {
                  return <></>;
                }
              }}
              columns={columnsDocs}
              data={pacoteDocsEdit.docs}
              positionActionsColumn='first'
              enableFullScreenToggle={false}
              localization={MRT_Localization_PT}
              enableEditing={checkPermission(MASTER, colaborador)}
              enablePagination={false}
              enableFilters={false}
              enableHiding={false}
              enableBottomToolbar={false}
              enableGlobalFilter={false}
              enableDensityToggle={false}
              editDisplayMode={'modal'}
              onEditingRowSave={({ exitEditingMode, row, values }) => {
                ProdutoService.produtoDocsPartialUpdate({
                  id: row.original.id,
                  formData: { nome: values.nome },
                }).then(
                  () => {
                    pacoteDocsEdit.docs[row.index] = values;
                    setPacoteDocEdit({
                      pacote: pacoteDocsEdit.pacote,
                      docs: pacoteDocsEdit.docs,
                    });
                  },
                  (reason: ApiError) => {
                    msgAPIError(reason);
                  }
                );
                exitEditingMode();
              }}
            />
          </Modal>
          <Modal
            withCloseButton={false}
            closeOnEscape={false}
            size={'lg'}
            closeOnClickOutside={false}
            opened={docsDiagAdd}
            onClose={() => {
              setDocsDialogAdd(false);
            }}
            title={'Adicionar Documento'}>
            <form onSubmit={docForm.onSubmit(savePacoteDoc)}>
              <TextInput
                label={'Nome Documento'}
                icon={<IconPdf />}
                {...docForm.getInputProps('nome')}
              />
              <FileInput
                label={'Ficheiro'}
                multiple={false}
                icon={<IconUpload />}
                {...docForm.getInputProps('file')}
              />

              <Group
                position='right'
                mt='md'>
                <Button
                  variant={'outline'}
                  leftIcon={<IconDeviceFloppy />}
                  type='submit'>
                  Guardar
                </Button>
                <Button
                  color={'yellow'}
                  variant={'outline'}
                  leftIcon={<IconX />}
                  type='button'
                  onClick={() => setDocsDialogAdd(false)}>
                  Cancelar
                </Button>
              </Group>
            </form>
          </Modal>
        </>
      );
    }
  };

  //--------------------------------------------------------------

  function dispaAllGruposPacote(pacote: Pacote) {
    if (
      confirm(
        `Tem a certeza que deseja adicionar o alojamento/pacote ${pacote.nome_interno} a TODOS os grupos da Campanha?`
      )
    ) {
      ProdutoService.produtoPacoteDisponibilizarATodosOsGruposRetrieve({ id: pacote.id }).then(
        () => {
          notifications.show({
            title: 'Alojamento/Pacote adicionado ao grupos!',
            message: `O alojamento/pacote ${pacote.nome_interno} foi adicionado a todo os grupos para selecção.`,
            withCloseButton: true,
            icon: <IconUsersGroup />,
          });
        },
        (reason) => {
          msgAPIError(reason);
        }
      );
    }
  }

  const columns = useMemo<MRT_ColumnDef<Pacote>[]>(
    () => [
      { accessorKey: 'ordem_de_aparencia', header: '#', size: 50, Header: '#' },
      { accessorKey: 'nome_interno', header: 'Nome Interno', size: 200 },
      { accessorKey: 'titulo_cliente', header: 'Título Cliente', size: 200 },
      {
        accessorKey: 'valor',
        header: 'Valor',
        size: 100,
        Cell: (cell) => `${cell.cell.getValue()}€`,
      },
        {
        accessorKey: 'refund_policy',
        header: 'Reembolso',
        size: 100,
      },
      {
        accessorKey: 'unidades',
        header: 'Uni. Disp.',
        size: 100,
        Cell: (cell) => (cell.cell.getValue() !== null ? `${cell.cell.getValue()}` : 'Ilimitado'),
      },
      {
        accessorKey: 'cond_limite',
        header: 'Condição',
        size: 100,
      },
      {
        accessorFn: (row) => `${row.nr_vendidos}` || '-',
        header: 'Vendidos',
        size: 50,
      },
      {
        accessorFn: (row) => `${row.nr_vendidos_e_pagos}` || '-',
        header: 'Reservados(com Pag.)',
        size: 50,
      },

      {
        accessorKey: 'activo',
        header: 'Ativo',
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Activo, disponivel para compra'}>
            <div>Act.</div>
          </Tooltip>
        ),
        size: 50,
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <IconCircleCheck color='green' />
          ) : (
            <IconCircleX color='red' />
          );
        },
      },
      {
        accessorKey: 'inicial',
        header: 'Auto',
        size: 50,
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Na criação de um grupo adicionar automaticamente para escolha'}>
            <div>Auto.</div>
          </Tooltip>
        ),
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <IconCircleCheck color='green' />
          ) : (
            <IconCircleX color='red' />
          );
        },
      },
      {
        accessorKey: 'inscricao_previa',
        csv: true,
        header: 'Incrição Previa',
        Header: (
          <Tooltip
            withinPortal={true}
            label={'Incrição Previa,via "Outro  método".'}>
            <div>Inscrição</div>
          </Tooltip>
        ),
        size: 50,
        Cell: (cell) => {
          return cell.cell.getValue() ? (
            <Tooltip
              withinPortal={true}
              label={cell.row.original.msg_metodo_inscricao}>
              <IconCircleCheck color='green' />
            </Tooltip>
          ) : (
            <IconCircleX color='red' />
          );
        },
      },

      {
        accessorKey: 'docs',
        csv: false,
        header: 'Documentos',
        Header: 'Documentos',
        Cell: (cell) => (
          <Button
            leftIcon={<IconFiles />}
            onClick={() => {
              setPacoteDocEdit({ pacote: cell.row.original, docs: cell.row.original.docs });
              setDocsDialog(true);
            }}
            variant='outline'>
            {cell.row.original.docs.length} Docs
          </Button>
        ),
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: pacotes,
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Button
          disabled={!checkPermission(MASTER, colaborador)}
          leftIcon={<IconPlus />}
          variant='outline'
          onClick={criarPacote}>
          Adicionar Alojamento/Pacote
        </Button>
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
      </>
    ),
    // renderTopToolbarCustomActions: ({ table }) => (
    //   <Button
    //     disabled={!pacotes.length || !checkPermission(MASTER, colaborador)}
    //     leftIcon={<IconCsv />}
    //     rightIcon={<IconDownload />}
    //     onClick={exportCSV}
    //     variant='outline'
    //   />
    // ),
    initialState: {
      density: 'xs',
    },
    enableRowActions: checkPermission(MASTER, colaborador),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '4px' }}>
        <Tooltip label='Editar Alojamento/Pacote'>
          <ActionIcon onClick={() => editPacote(row.original)}>
            <IconEdit />
          </ActionIcon>
        </Tooltip>
        <Tooltip label='Remover Produto'>
          <ActionIcon
            color='red'
            onClick={() => removePacote(row.original)}>
            <IconTrash />
          </ActionIcon>
        </Tooltip>
        <Tooltip label='Adicionar como opção a todos Grupos da Campanha'>
          <ActionIcon
            color='yellow'
            onClick={() => dispaAllGruposPacote(row.original)}>
            <IconUsersGroup />
          </ActionIcon>
        </Tooltip>
      </Box>
    ),

    enablePagination: false,
    enableColumnResizing: false,
    enableColumnFilters: false,
    enableBottomToolbar: false,
    enableGlobalFilter: false,
    enableSorting: true,
    positionActionsColumn: 'first',
    enableFullScreenToggle: false,
    localization: MRT_Localization_PT,
  });

  return (
    <Box>
      <MantineReactTable table={table} />
      {editPacoteDialog}
      {editDocsDialog()}
    </Box>
  );
}
